import React from "react";
import Head from "next/head";
import { GetStaticProps, InferGetStaticPropsType } from "next";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useRouter } from "next/router";

import Header from "../header";
import Footer from "../footer";
import styles from "../../styles/Text.module.css";

const DistanceSalesAgreement = (
  _props: InferGetStaticPropsType<typeof getStaticProps>,
) => {
  const { t } = useTranslation("common");
  const router = useRouter();
  const baseUrl = 'https://stoneity.com';
  const currentPath = router.asPath;
  const locale = router.locale || 'en';
  const canonicalUrl = locale === 'tr'
    ? `${baseUrl}/tr${currentPath}`
    : `${baseUrl}${currentPath}`;
  const alternateLocale = locale === 'tr' ? 'en' : 'tr';
  const alternatePath = '/distance-sales-agreement';

  return (
    <>
      <Head>
        <title>{t("seo_distance_sales_agreement_title")}</title>
        <meta name="description" content={t("seo_distance_sales_agreement_description")} />
        <meta name="keywords" content={locale === 'tr'
          ? "mesafeli satış sözleşmesi, dijital hizmet sözleşmesi, Stoneity, distance sales agreement"
          : "distance sales agreement, digital service agreement, Stoneity"} />
        <link key="canonical" rel="canonical" href={canonicalUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={t("seo_distance_sales_agreement_title")} />
        <meta property="og:description" content={t("seo_distance_sales_agreement_description")} />
        <meta property="og:image" content={`${baseUrl}/stoneityLogo.png`} />
        <meta property="og:site_name" content="Stoneity" />
        <meta property="og:locale" content={locale === 'tr' ? 'tr_TR' : 'en_US'} />
        <meta property="og:locale:alternate" content={locale === 'tr' ? 'en_US' : 'tr_TR'} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={t("seo_distance_sales_agreement_title")} />
        <meta name="twitter:description" content={t("seo_distance_sales_agreement_description")} />
        <meta name="twitter:image" content={`${baseUrl}/stoneityLogo.png`} />
        <meta name="twitter:site" content="@stoneity" />

        {/* Alternate languages */}
        <link rel="alternate" hrefLang={locale} href={canonicalUrl} />
        <link rel="alternate" hrefLang={alternateLocale} href={`${baseUrl}/${alternateLocale}${alternatePath}`} />
        <link rel="alternate" hrefLang="x-default" href={`${baseUrl}${alternatePath}`} />
      </Head>
      <Header />
      <main style={{ paddingInline: "1rem" }}>
        <div className={styles.globalTextContent}>
          <section>
            <div className={styles.globalTitle}>
              {t("distanceSalesContractTitle")}
            </div>
            <p className={styles.globalExplainParagraph}>
              {t("distanceSalesContractIntro")}
            </p>
          </section>

          <section>
            <div className={styles.globalSideTitle}>
              {t("partiesSectionTitle")}
            </div>
            <div className={styles.globalExplainParagraph}>
              <strong>{t("partiesMemberSiteTitle")}</strong>
              <br />
              {t("partiesMemberSiteContent")}
            </div>
            <div className={styles.globalExplainParagraph}>
              <strong>{t("partiesBuyerSellerTitle")}</strong>
              <br />
              {t("partiesBuyerSellerContent")}
            </div>
            <div className={styles.globalExplainParagraph}>
              <strong>{t("partiesContactInfoTitle")}</strong>
              <br />
              {t("partiesContactInfoContent")}
            </div>
          </section>

          <section>
            <div className={styles.globalSideTitle}>
              {t("legalFrameworkTitle")}
            </div>
            <p className={styles.globalExplainParagraph}>
              {t("legalFrameworkContent")}
            </p>
          </section>

          <section>
            <div className={styles.globalSideTitle}>
              {t("digitalServiceNatureTitle")}
            </div>
            <p className={styles.globalExplainParagraph}>
              {t("digitalServiceNatureContent")}
            </p>
          </section>

          <section>
            <div className={styles.globalSideTitle}>
              {t("deliveryReturnTitle")}
            </div>
            <div className={styles.globalExplainParagraph}>
              <strong>{t("deliveryInfoTitle")}</strong>
              <br />
              {t("deliveryInfoContent")}
            </div>
            <div className={styles.globalExplainParagraph}>
              <strong>{t("returnInfoTitle")}</strong>
              <br />
              {t("returnInfoContent")}
            </div>
          </section>

          <section>
            <div className={styles.globalSideTitle}>
              {t("finalProvisionsTitle")}
            </div>
            <p className={styles.globalExplainParagraph}>
              {t("finalProvisionsContent")}
            </p>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
};

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale || "tr", ["common"])),
  },
});

export default DistanceSalesAgreement;

